function flattenColorPalette(colors) {
  return Object.keys(colors).reduce((acc, key) => {
    if (typeof colors[key] === 'string') {
      return {
        ...acc,
        [key]: colors[key],
      };
    }

    return {
      ...acc,
      ...Object.keys(colors[key]).reduce((acc, variant) => {
        return {
          ...acc,
          [`${key}-${variant}`]: colors[key][variant],
        };
      }, {}),
    };
  }, {});
}

const opacity = {
  0: '0',
  10: '0.1',
  20: '0.2',
  30: '0.3',
  40: '0.4',
  50: '0.5',
  60: '0.6',
  70: '0.7',
  80: '0.8',
  90: '0.9',
  100: '1',
};

module.exports = {
  theme: {
    inset: {
      0: 0,
      auto: 'auto',
      '1/2': '50%',
    },
    colors: {
      black: '#000000',
      white: '#FFFFFF',
      grey: {
        100: '#F1F1F1',
        200: '#738287',
        300: '#8F9190',
      },
      babyblue: {
        50: '#E7EEF4',
        100: '#E8F5F5',
        200: '#405671',
      },
      emerald: {
        100: '#8DE8C9',
        200: '#224F49',
      },
      lavender: {
        100: '#F0EDF8',
        200: '#4F4071',
      },
      navy: {
        100: '#0F1548',
        200: '#10132F',
      },
      orange: {
        100: '#FDEDDA',
        200: '#CF7C2F',
      },
      salmon: {
        100: '#FAE7E1',
        200: '#F1BABA',
        300: '#A74D31',
      },
      slate: {
        100: '#353746',
        200: '#0B3B3C',
      },
      teal: {
        100: '#CCF6E8',
        200: '#00DABF',
      },
      turquoise: {
        100: '#D2F9E2',
        200: '#00D9BF',
      },
      green: {
        100: '#00DA6F',
        200: '#12CF6C',
        300: '#67D7C0',
        400: '#277067',
        500: '#33B69E',
      },
      sale: {
        100: '#EEB4B8',
        200: '#B9374F',
        300: '#742130',
      },
      olive: {
        100: '#EDF2ED',
        200: '#587265',
        300: '#7CB77B',
        400: '#77A396',
        500: '#295353',
      },
      transparent: 'rgba(0, 0, 0, 0)',
      current: 'currentColor',
      grid: '#FF0000',
    },
    borderRadius: {
      none: '0',
      sm: '0.125rem',
      default: '0.25rem',
      md: '0.3125rem',
      lg: '0.5rem',
      xl: '0.75rem',
      full: '9999px',
    },
    boxShadow: {
      sm: '',
      md: '2px 4px 10px rgba(0, 0, 0, 0.25)',
      lg: '0 2.5px 25px rgba(0, 0, 0, 0.15)',
      xl: '0 3px 30px rgba(0, 0, 0, 0.15)',
      '2xl': '0 5px 50px rgba(0, 0, 0, 0.15)',
    },
    container: {
      center: true,
    },
    fontFamily: {
      sans: [
        'Larsseit',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        '"Noto Sans"',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
        '"Noto Color Emoji"',
      ],
      serif: [
        'Garamond',
        'Baskerville',
        '"Baskerville Old Face"',
        '"Hoefler Text"',
        '"Times New Roman"',
        'serif',
      ],
      mono: [
        'ui-monospace',
        'SFMono-Regular',
        'Menlo',
        'Monaco',
        'Consolas',
        '"Liberation Mono"',
        '"Courier New"',
        'monospace',
      ],
    },
    textOpacity: opacity,
    extend: {
      backgroundOpacity: opacity,
      borderWidth: {
        3: '3px',
      },
      cursor: {
        none: 'none',
      },
      opacity,
      scale: {
        flip: '-1',
      },
      screens: {
        '2xl': '1440px',
      },
      spacing: {
        11: '2.75rem',
        '23r': '23rem',
        '1/4': '25%',
        '2/4': '50%',
        '3/4': '75%',
        '1/3': 'calc(100% / 3)',
        '2/3': 'calc(100% / 3 * 2)',
        '1/3vh': 'calc(100vh / 3)',
        '2/3vh': 'calc(100vh / 3 * 2)',
      },
      maxWidth: {
        '1/3': 'calc(100% / 3)',
        '2/3': 'calc(100% / 3 * 2)',
        '1/4': '25%',
        '1/2': '50%',
        '3/4': '75%',
        '1/5': '20%',
        '2/5': '40%',
        '3/5': '60%',
        '4/5': '80%',
      },
      transitionProperty: {
        button: 'background-color, border-color, box-shadow, color, transform',
        filter: 'filter',
        ui: 'opacity, transform',
      },
      zIndex: {
        grid: 9999,
        under: -1,
      },
    },
  },
  variants: {
    backgroundColor: ['responsive', 'hover', 'group-hover'],
    borderColor: ['responsive', 'hover', 'group-hover'],
    borderWidth: ['responsive', 'hover', 'first', 'last'],
    margin: ['responsive', 'first', 'last'],
    opacity: ['responsive', 'hover', 'group-hover'],
    padding: ['responsive', 'odd', 'even'],
    scale: ['hover', 'group-hover'],
    textColor: ['responsive', 'hover', 'group-hover'],
  },
  plugins: [
    ({ e, addUtilities, theme }) => {
      const colors = flattenColorPalette(theme('colors'));

      const selectionColors = Object.keys(colors).reduce((acc, key) => {
        return {
          ...acc,
          [`.selection-bg-${e(key)} ::selection`]: {
            'background-color': colors[key],
          },
          [`.selection-text-${e(key)} ::selection`]: {
            color: colors[key],
          },
        };
      }, {});

      addUtilities(selectionColors);
    },
  ],
  purge: {
    enabled: process.env.NODE_ENV === 'production',
    content: [
      'components/**/*.vue',
      'layouts/**/*.vue',
      'pages/**/*.vue',
      'util/**/*.js',
      'app.html',
    ],
  },
};
